// Shape of the guided pricing journey: which questions belong to the shared
// "Your business" stage, and how each service's questions divide into sections.
//
// Everything here is PRESENTATION. The questions, their groups, their conditions
// and every figure come from the CASS pricing platform — this file only decides
// where each question is shown and how much of a section is done.
import { isVisible, hasValue } from "./packageLogic";

// Groups treated as business-level unless the admin says otherwise. These are the
// group names the pricing platform uses for questions that aren't specific to one
// piece of work.
export const BUSINESS_GROUPS = ["General"];

const lower = (list) => (list || []).map((x) => String(x).toLowerCase());

/**
 * Which question keys belong on the "Your business" stage.
 *
 * Three sources, so CASS can adjust it without a developer:
 *  - any key listed in business_stage_keys (forced in)
 *  - any question whose group is listed in business_stage_groups
 *  - any question asked by more than one of the selected services
 * minus anything listed in business_stage_exclude_keys.
 */
export function businessKeys(services, selectedIds, cfg = {}) {
  const groups = lower(cfg.business_stage_groups?.length ? cfg.business_stage_groups : BUSINESS_GROUPS);
  const forced = new Set(cfg.business_stage_keys || []);
  const excluded = new Set(cfg.business_stage_exclude_keys || []);
  const seenIn = new Map();
  const byGroup = new Set();

  (services || [])
    .filter((s) => selectedIds.includes(s.service_id))
    .forEach((s) => {
      (s.questions || []).forEach((q) => {
        if (!q.key) return;
        seenIn.set(q.key, (seenIn.get(q.key) || 0) + 1);
        if (groups.includes(String(q.group || "").toLowerCase())) byGroup.add(q.key);
      });
    });

  const keys = new Set();
  seenIn.forEach((count, key) => {
    if (excluded.has(key)) return;
    if (forced.has(key) || byGroup.has(key) || count > 1) keys.add(key);
  });
  return keys;
}

function toGroups(questions) {
  const groups = [];
  questions.forEach((q) => {
    const name = q.group || "";
    let g = groups.find((x) => x.name === name);
    if (!g) {
      g = { name, questions: [] };
      groups.push(g);
    }
    g.questions.push(q);
  });
  return groups;
}

const progressOf = (questions, answers) => {
  const required = questions.filter((q) => q.required);
  const answered = required.filter((q) => hasValue(answers[q.key]));
  return {
    required: required.length,
    answered: answered.length,
    // A section with nothing compulsory is complete as soon as it is reached.
    complete: required.length === answered.length,
    missing: required.filter((q) => !hasValue(answers[q.key])).map((q) => q.key),
  };
};

/**
 * The whole journey for the services currently selected.
 *
 * A question asked by two services is asked once — the business stage takes it if
 * it qualifies, otherwise the first service in catalogue order.
 */
export function buildJourney(services, selectedIds, answers, cfg = {}, hideKeys = []) {
  const shared = businessKeys(services, selectedIds, cfg);
  const hidden = new Set(hideKeys);
  const fixed = cfg.fixed_answers || {};
  const seen = new Set();
  const business = [];
  // Answered on the visitor's behalf: never shown, but still sent to the pricing
  // API exactly as the live /pricing journey does (parity).
  const implied = [];
  const chosen = (services || []).filter((s) => selectedIds.includes(s.service_id));

  const sections = chosen.map((s) => {
    const own = [];
    const notes = [];
    [...(s.questions || [])]
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .forEach((q) => {
        if (!q.key || seen.has(q.key)) return;
        if (!isVisible(q, answers)) return;
        seen.add(q.key);
        // Settled by how CASS works rather than by the visitor. Still priced the
        // same way, and said out loud in the section of the service it belongs to
        // — never on the shared business stage, where it reads out of context.
        if (fixed[q.key]) {
          implied.push(q);
          if (fixed[q.key].message) notes.push({ key: q.key, message: fixed[q.key].message });
          return;
        }
        if (hidden.has(q.key)) { implied.push(q); return; }
        (shared.has(q.key) ? business : own).push(q);
      });
    return { service: s, questions: own, groups: toGroups(own), notes, ...progressOf(own, answers) };
  });

  return {
    business: { questions: business, groups: toGroups(business), ...progressOf(business, answers) },
    sections,
    implied,
  };
}

// Every visible question across the journey — what the quote is built from.
export const journeyQuestions = (journey) => [
  ...journey.business.questions,
  ...journey.sections.flatMap((sec) => sec.questions),
];

export function journeyPayload(journey, answers) {
  const out = {};
  [...journeyQuestions(journey), ...(journey.implied || [])].forEach((q) => {
    if (hasValue(answers[q.key])) out[q.key] = answers[q.key];
  });
  return out;
}

export function journeyMissing(journey, answers) {
  const out = [];
  journey.business.questions.forEach((q) => {
    if (q.required && !hasValue(answers[q.key])) out.push({ stage: "business", key: q.key, label: q.label });
  });
  journey.sections.forEach((sec, i) => {
    sec.questions.forEach((q) => {
      if (q.required && !hasValue(answers[q.key])) {
        out.push({ stage: "service", index: i, service_id: sec.service.service_id, key: q.key, label: q.label });
      }
    });
  });
  return out;
}

// How far through the questions they are, for the summary panel.
export function journeyProgress(journey) {
  const parts = [journey.business, ...journey.sections];
  const required = parts.reduce((n, p) => n + p.required, 0);
  const answered = parts.reduce((n, p) => n + p.answered, 0);
  return { required, answered, percent: required ? Math.round((answered / required) * 100) : 100 };
}

// Quote lines carry a service name but no id, so the price screen maps back to the
// catalogue by name to offer "edit these answers". Asked for as an API addition;
// until it lands, an exact name match is the only join available.
export function idsByName(services) {
  const out = {};
  (services || []).forEach((s) => { out[s.name] = s.service_id; });
  return out;
}
